import { DeferredPromise, sleep } from '@cf-shared/shared-util';
import * as Quagga from 'getuserbarcode';
import { animationFrame } from 'services/browser-utils';
import { LoggingService } from 'services/logging.service';
import { BarcodeDetectionResult } from 'services/camera-strategy';

/** Quagga's per-frame result shape — re-exposed through `onProcessed` so the
 *  barcode-scanner page can run its confirmation heuristic on `codeResult`
 *  + `boxes` and still draw its custom detection overlay (reticle line, box). */
export type QuaggaProcessResult = {
  codeResult?: { code?: string; format?: string };
  line?: { x: number; y: number }[];
  boxes?: number[][][];
} | null | undefined;

export interface StartDecodingOptions {
  /** DOM element Quagga inserts its `<video>` + overlay canvases into. */
  target: Element;
  /** `deviceId` to open. Null means "let the browser pick via facingMode" */
  deviceId: string | null;
  /** Per-confirmed-detection callback. Fires after Quagga signals a full decode. */
  onDetected: (result: BarcodeDetectionResult) => void;
  /** Per-frame callback. Used by the page to draw its own detection overlay. */
  onProcessed?: (result: QuaggaProcessResult) => void;
  /** When true, relax Quagga's input constraints (used when first open fails
   *  with OverconstrainedError). */
  relaxedConstraints?: boolean;
  /** Caller destruction check — lets the decoder bail early if the host
   *  component was destroyed before Quagga finished initializing. */
  isDestroyed?: () => boolean;
}

/**
 * Thin wrapper around Quagga's lifecycle — extracted from the old
 * CameraService barcode helpers so the strategy / cs-camera-view can consume
 * Quagga via a stable, strategy-agnostic interface.
 *
 * Quagga internally owns its `getUserMedia` stream and its `<video>` element
 * (the library creates them during `Quagga.init`). The decoder exposes the
 * active track via `getActiveTrack()` so callers can run zoom/flash/crop
 * logic against it.
 */
export class BarcodeDecoderService {
  initialized = false;

  constructor(private readonly logging: LoggingService) {}

  /** Initialize Quagga against the given DOM target + camera id, wire up the
   *  detection callbacks, and start decoding. Awaits Quagga.init before
   *  calling Quagga.start so caller-side capability setup (zoom/flash) can
   *  run against the track between. */
  async startDecoding(options: StartDecodingOptions): Promise<void> {
    await this.quaggaInitAsync(options);

    const track = this.getActiveTrack();
    // if Quagga did not initialize (DOM not ready or caller destroyed), bail without error
    if (!track) return;

    if (options.onProcessed) {
      Quagga.onProcessed(result => options.onProcessed(result));
    }

    Quagga.onDetected(result => {
      const code = result?.codeResult?.code;
      if (!code) return;
      options.onDetected({
        value: code,
        type: result.codeResult.format ?? 'unknown',
      });
    });

    Quagga.start();
    this.initialized = true;

    // fix for black screen in zoneless mode: force rendering
    await animationFrame();
  }

  /** Stop decoding + tear down Quagga's internal stream. Optionally accept the
   *  viewport `container` Quagga injected its `<video>` into so the video's
   *  `srcObject` is released in the same call — without this, mid-rotation
   *  teardown leaks the stream reference until the host component unmounts. */
  async stopDecoding(container?: Element | Document): Promise<void> {
    if (this.initialized) {
      Quagga.stop();
      this.initialized = false;
    }
    if (container) {
      this.releaseStreamsInElement(container);
    }
  }

  /** Return the MediaStreamTrack Quagga's CameraAccess opened. Null before
   *  `startDecoding` has resolved or after `stopDecoding`. Callers use this
   *  to apply zoom / torch constraints against the Quagga-owned track. */
  getActiveTrack(): MediaStreamTrack | null {
    return Quagga.CameraAccess.getActiveTrack() ?? null;
  }

  /** Decoder-internal frame dimensions — the coordinate space Quagga's
   *  per-frame `result.boxes` corners are in. Under `locator.halfSample:
   *  true` this is half the camera stream's intrinsic resolution. The
   *  bracket-tracker overlay reads this per-frame to map decoder coords
   *  into video-element DOM space. Returns null before `startDecoding`
   *  has resolved or when Quagga's overlay canvas isn't yet attached. */
  getSourceDims(): { width: number; height: number } | null {
    const overlay = (Quagga as { canvas?: { dom?: { overlay?: { width: number; height: number } } } }).canvas?.dom?.overlay;
    if (!overlay) return null;
    if (overlay.width <= 0 || overlay.height <= 0) return null;
    return { width: overlay.width, height: overlay.height };
  }

  /** Stop any `<video>` srcObject streams inside the given container. Used
   *  after `Quagga.stop` so Quagga's injected `<video>` doesn't keep a live
   *  MediaStream bound. Safe on arbitrary containers — only affects videos
   *  with live MediaStream srcObjects. */
  private releaseStreamsInElement(container: Element | Document): void {
    const videos = Array.from(container.querySelectorAll('video'));
    videos.forEach(video => {
      if (video instanceof HTMLVideoElement && video.srcObject instanceof MediaStream) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.srcObject = null;
      }
    });
  }

  /** Run `Quagga.init` against the DOM target + constraints. Waits for the
   *  target element to appear (up to ~2s) since it may be absent on app
   *  resume / slow first-paint. Promisifies Quagga's callback API and
   *  temporarily patches `getContext` to pass `willReadFrequently: true`
   *  (Chrome warns otherwise; Quagga reads the 2D context every frame). */
  private async quaggaInitAsync(options: StartDecodingOptions): Promise<void> {
    const { target: targetElement, deviceId, relaxedConstraints, isDestroyed } = options;
    const destroyedCheck = isDestroyed ?? (() => false);
    const initSucceeded = DeferredPromise.defer<void>();

    // patch getContext to set willReadFrequently for 2d contexts during Quagga init
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalGetContext = HTMLCanvasElement.prototype.getContext;
    (HTMLCanvasElement.prototype as any).getContext = function(contextType: string, contextOptions?: any) {
      if (contextType === '2d') {
        contextOptions = { ...contextOptions, willReadFrequently: true };
      }
      return originalGetContext.call(this, contextType, contextOptions);
    };

    // wait for target element (can be missing during app resume or slow render)
    let target = targetElement;
    if (!target) {
      for (let i = 0; i < 20 && !target; i++) {
        await sleep(100);
        if (destroyedCheck()) {
          (HTMLCanvasElement.prototype as any).getContext = originalGetContext;
          return;
        }
        target = targetElement;
      }
      if (!target) {
        (HTMLCanvasElement.prototype as any).getContext = originalGetContext;
        if (destroyedCheck()) return;
        this.logging.trackSilentProblem('Camera window element not found after waiting in quaggaInit', null);
        return;
      }
    }

    // `'environment'` / `'user'` are facingMode sentinels (how
    // CameraService represents cameras on Capacitor native + how the seed
    // in cs-camera-view's barcode init populates cameraInfos before real
    // enumeration). They are NOT valid deviceId values — passing them to
    // Quagga as `deviceId: { exact }` throws OverconstrainedError on both
    // PWA and Capacitor native. Treat them as facingMode instead.
    const facingModeSentinel = deviceId === 'environment' || deviceId === 'user';
    const useFacingMode = !deviceId || facingModeSentinel;
    const facingMode: 'environment' | 'user' = deviceId === 'user' ? 'user' : 'environment';

    try {
      Quagga.init(
        {
          inputStream: {
            name: 'Live',
            type: 'LiveStream',
            constraints: {
              ...(useFacingMode
                ? { facingMode: { ideal: facingMode } }
                : { deviceId: { exact: deviceId } }),
              ...(relaxedConstraints ? {} : { width: 800, height: 800 }),
            },
            target,
          },
          frequency: 30,
          locate: true,
          locator: {
            patchSize: 'large',
            halfSample: true,
          },
          decoder: {
            readers: [
              { format: 'upc_reader', config: {} } as any,
              { format: 'ean_reader', config: {} } as any,
            ],
          },
        },
        (err: string) => {
          (HTMLCanvasElement.prototype as any).getContext = originalGetContext;
          if (err) {
            console.log(`Barcode reader: Can't initialize - ${err}`);
            this.initialized = false;
            initSucceeded.reject(err);
          } else {
            initSucceeded.resolve(null);
          }
        },
      );
    } catch (e) {
      (HTMLCanvasElement.prototype as any).getContext = originalGetContext;
      throw e;
    }

    return initSucceeded;
  }
}
